import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Grid, Button, TextField, MenuItem,
  ToggleButtonGroup, ToggleButton, CircularProgress, Alert,
  Avatar, Chip, IconButton, InputAdornment,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Assessment as ReportIcon, Print as PrintIcon,
  Description as WordIcon, Sms as SmsIcon,
  Search as SearchIcon, Person as PersonIcon,
  PrintOutlined as BulkPrintIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { REPORT_CSS, reportCardHTML, openPrint, downloadWord } from './reportCardHtml';
import BulkReportPreview from './BulkReportPreview';

// Admin Student Report page.
//
// Two-panel workspace: filters + roster on the left, live report
// preview on the right. Unlike the teacher portal — which is always
// pinned to the currently active period — this page has zero implicit
// scope. The operator picks class, academic year, term, and (when
// relevant) mock/mid-term number by hand, so an alum can walk in five
// years later and request their JHS 2 Term 3 slip and the school can
// generate it without touching the active-period settings.
//
// Bulk print reuses the shared BulkReportPreview overlay so the
// on-screen scroll looks and prints exactly like the teacher-portal
// flow. Printing is always an explicit action taken from that overlay.

const EXAM_TYPES = [
  { v: 'terminal', label: 'Terminal' },
  { v: 'mock',     label: 'Mock' },
  { v: 'mid',      label: 'Mid-Term' },
];

export default function StudentReportPage() {
  const theme = useTheme();

  // Scope filters — every one of them is user-controlled so history
  // works. `lookups` seeds classes / terms / acc_years the operator
  // can pick from; anything typed manually still submits.
  const [lookups, setLookups] = useState({ classes: [], terms: [], acc_years: [] });
  const [cla, setCla]         = useState('');
  const [term, setTerm]       = useState('');
  const [acc_yer, setAccYer]  = useState('');
  const [examType, setExamType] = useState('terminal');
  const [examNum, setExamNum] = useState('');

  const [roster, setRoster]     = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [q, setQ]               = useState('');
  const [student, setStudent]   = useState(null);

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState('');

  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkList, setBulkList] = useState(null);

  // Which classes actually sat this exam, and which sittings they sat.
  // Only Mock / Mid-Term can be narrowed this way — see the comment on
  // GET /examination/reports/scopes for why a terminal exam cannot be.
  const [scopes, setScopes] = useState({ applies: false, classes: [] });

  useEffect(() => {
    API.get('/examination/lookups')
      .then(r => setLookups(l => ({ ...l, ...r.data })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (examType === 'terminal' || !term || !acc_yer) {
      setScopes({ applies: false, classes: [] });
      return;
    }
    let cancelled = false;
    API.get('/examination/reports/scopes', { params: { exam_type: examType, term, acc_yer } })
      .then(r => { if (!cancelled) setScopes(r.data || { applies: false, classes: [] }); })
      .catch(() => { if (!cancelled) setScopes({ applies: false, classes: [] }); });
    return () => { cancelled = true; };
  }, [examType, term, acc_yer]);

  // The classes the Class picker offers. On Mock / Mid-Term this is
  // only the classes that sat it; on Terminal it stays the full list.
  const classOptions = useMemo(
    () => (scopes.applies ? (scopes.classes || []).map(c => c.cla) : (lookups.classes || [])),
    [scopes, lookups.classes]
  );

  // The sittings available for the chosen class — what the number
  // picker offers instead of a free-text box.
  const sittingOptions = useMemo(() => {
    if (!scopes.applies || !cla) return [];
    return scopes.classes.find(c => c.cla === cla)?.sittings || [];
  }, [scopes, cla]);

  // Keep the pickers honest when the scope moves underneath them: a
  // class that did not sit this exam, or a sitting that does not exist,
  // must not stay selected and silently produce a blank report.
  useEffect(() => {
    if (scopes.applies && cla && !classOptions.includes(cla)) setCla('');
  }, [scopes.applies, classOptions, cla]);

  useEffect(() => {
    if (examType === 'terminal') { setExamNum(''); return; }
    if (!sittingOptions.length) return;
    // One sitting is not a choice — pick it. Otherwise drop a stale one.
    if (sittingOptions.length === 1) {
      setExamNum(String(sittingOptions[0].exam_num));
    } else if (examNum && !sittingOptions.some(s => String(s.exam_num) === String(examNum))) {
      setExamNum('');
    }
  }, [examType, sittingOptions, examNum]);

  // Roster load — driven off `cla` alone; term / year filter the
  // report itself, not the roster.
  const loadRoster = useCallback(async () => {
    if (!cla) { setRoster([]); return; }
    setRosterLoading(true);
    try {
      const r = await API.get('/examination/scores/roster', { params: { cla } });
      setRoster(Array.isArray(r.data) ? r.data : []);
    } catch { setRoster([]); }
    finally { setRosterLoading(false); }
  }, [cla]);
  useEffect(() => { loadRoster(); }, [loadRoster]);

  // Reset student pick whenever the class changes.
  useEffect(() => { setStudent(null); setData(null); }, [cla]);

  const filteredRoster = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return roster;
    return roster.filter(s =>
      (s.name || '').toLowerCase().includes(needle)
      || (s.ind || '').toLowerCase().includes(needle)
    );
  }, [roster, q]);

  const scopeReady = !!(cla && term && acc_yer && (examType === 'terminal' || examNum));
  const canLoad = scopeReady && !!student?.ind;

  const load = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true); setErr(''); setData(null);
    try {
      const r = await API.get(`/examination/reports/student/${encodeURIComponent(student.ind)}`, {
        params: { exam_type: examType, term, acc_yer, exam_num: examNum },
      });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not load report');
    } finally { setLoading(false); }
  }, [canLoad, student, examType, examNum, term, acc_yer]);
  useEffect(() => { load(); }, [load]);

  const handlePrint = () => {
    if (!data) return;
    openPrint(reportCardHTML(data), `Report-${data.student?.fullname || ''}`);
  };
  const handleWord = () => {
    if (!data) return;
    const fname = `Report-${(data.student?.fullname || 'student').replace(/\s+/g,'_')}.doc`;
    downloadWord(reportCardHTML(data), fname, fname);
  };
  const sendSMS = async () => {
    if (!data) return;
    const msg =
      `${data.student.fullname} — ${data.scope.term} (${data.scope.acc_yer}) ${data.scope.exam_type.toUpperCase()} report: ` +
      `Total ${Number(data.totals?.total || 0).toFixed(1)}${data.totals?.position ? `, Position ${data.totals.position}` : ''}` +
      `${data.totals?.aggregate != null ? `, Aggregate ${data.totals.aggregate}` : ''}.`;
    try {
      const r = await API.post('/examination/reports/sms', { ind: data.student.index_no, message: msg });
      swal.success(`SMS queued to ${r.data.mobi}`);
    } catch (e) { swal.error(swal.pickError(e, 'SMS queue failed')); }
  };

  // Bulk flow — same as the teacher portal.
  const handleShowAll = async () => {
    if (!scopeReady) {
      swal.warning('Pick class, term and academic year first.');
      return;
    }
    setBulkBusy(true);
    try {
      const r = await API.get('/examination/reports/class', {
        params: { cla, exam_type: examType, term, acc_yer, exam_num: examNum },
      });
      const list = (Array.isArray(r.data) ? r.data : []).filter(x => !x?.error);
      if (list.length === 0) {
        swal.warning('No students matched this class for the term / year.');
        return;
      }
      setBulkList(list);
    } catch (e) {
      swal.error(swal.pickError(e, 'Show all failed'));
    } finally { setBulkBusy(false); }
  };
  const bulkPrintAll = () => {
    if (!bulkList) return;
    openPrint(bulkList.map(d => reportCardHTML(d)).join('\n'),
      `Class-${cla}-${term}-${examType}`);
  };
  const bulkPrintOne = (d) => openPrint(reportCardHTML(d), `Report-${d.student?.fullname || ''}`);
  const bulkWordOne  = (d) => {
    const fname = `Report-${(d.student?.fullname || 'student').replace(/\s+/g,'_')}.doc`;
    downloadWord(reportCardHTML(d), fname, fname);
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 1, md: 1.5 }, py: { xs: 1, md: 1.4 } }}>
      {/* Compact header — gradient strip with title + scope chips.
          Replaces the tall ModuleHeader banner so more of the vertical
          budget goes to the roster + preview. */}
      <Card sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: '#fff', px: { xs: 1.5, md: 2.4 }, py: 1.2,
          display: 'flex', alignItems: 'center', gap: 1.4, flexWrap: 'wrap',
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 36, height: 36 }}>
            <ReportIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.15, fontSize: 16 }}>
              Student Report
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', lineHeight: 1.15 }}>
              Terminal · Mid-Term · Mock — pick class + year + term to browse history and print in bulk.
            </Typography>
          </Box>
          <Button size="small" variant="contained" color="inherit"
            startIcon={bulkBusy ? <CircularProgress size={14} /> : <BulkPrintIcon fontSize="small" />}
            disabled={bulkBusy || !scopeReady}
            onClick={handleShowAll}
            sx={{
              bgcolor: 'rgba(255,255,255,0.14)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.55)',
              textTransform: 'none', fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
            }}>
            {bulkBusy ? 'Loading…' : 'Show all students'}
          </Button>
          <Button size="small" variant="contained" color="inherit"
            startIcon={<PrintIcon fontSize="small" />} disabled={!data}
            onClick={handlePrint}
            sx={{
              bgcolor: 'rgba(255,255,255,0.92)', color: 'primary.main',
              textTransform: 'none', fontWeight: 700,
              '&:hover': { bgcolor: '#fff' },
            }}>
            Print
          </Button>
          <Button size="small" variant="outlined" color="inherit"
            startIcon={<WordIcon fontSize="small" />} disabled={!data}
            onClick={handleWord}
            sx={{
              borderColor: 'rgba(255,255,255,0.6)', color: '#fff',
              textTransform: 'none', fontWeight: 700,
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}>
            Word
          </Button>
          <Button size="small" variant="outlined" color="inherit"
            startIcon={<SmsIcon fontSize="small" />} disabled={!data}
            onClick={sendSMS}
            sx={{
              borderColor: 'rgba(255,255,255,0.6)', color: '#fff',
              textTransform: 'none', fontWeight: 700,
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}>
            SMS
          </Button>
        </Box>
      </Card>

      {/* Two-column workspace: filters+roster (left) · preview (right) */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3.5}>
          <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Scope filters */}
            <Box sx={{ p: 1.6, borderBottom: 1, borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase',
                letterSpacing: 0.5, color: 'text.secondary', mb: 0.8 }}>
                Exam type
              </Typography>
              <ToggleButtonGroup fullWidth size="small" exclusive value={examType}
                onChange={(_, v) => v && setExamType(v)}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, py: 0.4 } }}>
                {EXAM_TYPES.map(t => (
                  <ToggleButton key={t.v} value={t.v}>{t.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Grid container spacing={1.2} sx={{ mt: 0.2 }}>
                <Grid item xs={12}>
                  <TextField select fullWidth size="small" label="Class" value={cla}
                    onChange={e => setCla(e.target.value)}
                    helperText={
                      scopes.applies
                        ? (classOptions.length
                            ? `${classOptions.length} class${classOptions.length === 1 ? '' : 'es'} sat this exam`
                            : 'No class sat this exam for the term and year picked')
                        : ' '
                    }>
                    <MenuItem value=""><em>— Pick —</em></MenuItem>
                    {classOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField select fullWidth size="small" label="Term" value={term}
                    onChange={e => setTerm(e.target.value)}>
                    <MenuItem value=""><em>— Pick —</em></MenuItem>
                    {lookups.terms.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField select fullWidth size="small" label="Academic year" value={acc_yer}
                    onChange={e => setAccYer(e.target.value)}>
                    <MenuItem value=""><em>— Pick —</em></MenuItem>
                    {lookups.acc_years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </TextField>
                </Grid>
                {examType !== 'terminal' && (
                  <Grid item xs={12}>
                    {/* The system already knows which sittings exist —
                        the operator should never have to remember a
                        number, or discover from a blank page that they
                        typed one nothing was entered under. */}
                    <TextField select fullWidth size="small"
                      label={examType === 'mock' ? 'Mock number' : 'Mid-Term number'}
                      value={examNum} onChange={e => setExamNum(e.target.value)}
                      disabled={!cla || !sittingOptions.length}
                      helperText={
                        !cla ? 'Pick a class first'
                          : !sittingOptions.length ? 'No sitting found for this class'
                          : ' '
                      }>
                      <MenuItem value=""><em>— Pick —</em></MenuItem>
                      {sittingOptions.map(s => (
                        <MenuItem key={s.exam_num} value={String(s.exam_num)}>
                          {s.label}
                          {s.has_marks
                            ? ` · ${s.n_subjects} subject${s.n_subjects === 1 ? '' : 's'}`
                            : ' · no marks entered yet'}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
              </Grid>

              {!scopeReady && (
                <Alert severity="info" sx={{ mt: 1.4, py: 0.4 }}>
                  Fill class, term and year to see the roster below.
                </Alert>
              )}
            </Box>

            {/* Roster search */}
            <Box sx={{ p: 1.6, borderBottom: 1, borderColor: 'divider' }}>
              <TextField fullWidth size="small" placeholder="Search student…"
                value={q} onChange={e => setQ(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: cla && (
                    <IconButton size="small" onClick={loadRoster} title="Reload roster">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  ),
                }} />
            </Box>

            {/* Roster list */}
            <Box sx={{ maxHeight: { xs: 320, md: 520 }, overflow: 'auto', px: 0.6, py: 0.8 }}>
              {rosterLoading && (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={22} /></Box>
              )}
              {!rosterLoading && cla && filteredRoster.length === 0 && (
                <Alert severity="info" sx={{ m: 1 }}>
                  {q ? 'No students match.' : `No students found in ${cla}.`}
                </Alert>
              )}
              {!cla && (
                <Alert severity="info" sx={{ m: 1 }}>
                  Pick a class above to load the roster.
                </Alert>
              )}
              <Stack spacing={0.4}>
                {filteredRoster.map(s => (
                  <Box key={s.ind}
                    onClick={() => setStudent(s)}
                    sx={{
                      px: 1.2, py: 0.9, borderRadius: 1.4, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 1,
                      bgcolor: student?.ind === s.ind ? alpha(theme.palette.primary.main, 0.14) : 'transparent',
                      border: '1px solid',
                      borderColor: student?.ind === s.ind ? 'primary.main' : 'transparent',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                    }}>
                    <PersonIcon fontSize="small"
                      sx={{ color: student?.ind === s.ind ? 'primary.main' : 'action.active' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontWeight: student?.ind === s.ind ? 800 : 600,
                        fontSize: 13, lineHeight: 1.2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {s.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                        {s.ind}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Card>
        </Grid>

        {/* Right: preview */}
        <Grid item xs={12} md={8} lg={8.5}>
          <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{
              px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1,
              borderBottom: 1, borderColor: 'divider',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
            }}>
              <Typography sx={{ fontWeight: 800, fontSize: 12,
                textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', flex: 1 }}>
                Preview
              </Typography>
              {student && data && (
                <Chip size="small" color="primary" variant="outlined"
                  label={`${data.student?.fullname} · ${(data.scope?.term || '').replace('_', ' ')} · ${data.scope?.acc_yer}`}
                  sx={{ fontWeight: 700 }} />
              )}
            </Box>

            <Box sx={{ maxHeight: { xs: 'auto', md: 720 }, overflow: 'auto', p: 2, bgcolor: '#eef0f4' }}>
              {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
              {!student && !err && (
                <Alert severity="info" icon={<ReportIcon />}
                  sx={{ maxWidth: 640, mx: 'auto' }}>
                  Pick a student on the left to preview their report card. Filters apply immediately — no <b>Generate</b> button needed.
                </Alert>
              )}
              {loading && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Building report…
                  </Typography>
                </Box>
              )}
              {data && !loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ bgcolor: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                    <style>{REPORT_CSS}</style>
                    <div dangerouslySetInnerHTML={{ __html: reportCardHTML(data) }} />
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Bulk preview overlay */}
      {bulkList && (
        <BulkReportPreview
          list={bulkList}
          title="Class Report Preview"
          subtitle={`${cla} · ${(term || '').replace('_', ' ')} · ${acc_yer}`}
          onClose={() => setBulkList(null)}
          onPrintAll={bulkPrintAll}
          onPrintOne={bulkPrintOne}
          onWordOne={bulkWordOne} />
      )}
    </Box>
  );
}
